import type { ReactNode } from "react";

import { cn } from "../cn";

export interface Fact {
  /** What the figure is, in the reader's words. Uppercased by the component. */
  label: string;
  value: ReactNode;
}

export interface FactStripProps {
  facts: Fact[];
  className?: string;
}

/**
 * A handful of figures about the thing on screen, as one banded strip.
 *
 * Rooms and Requests both open a detail screen with four facts at the top —
 * players, keys, last change, environment; took, steps, game events, player —
 * and both had drawn them as a loose `flex` of label/value pairs with a gap.
 * Loose pairs read as four unrelated captions; a banded strip reads as one
 * summary of one object, which is what they are.
 *
 * It is not a `Card`. `AGENTS.md`'s rule is that containment has to mean
 * something, and these figures are a header for what follows rather than a
 * detachable object — so this draws one quiet perimeter and divides inside it,
 * instead of four surfaces or a floating panel.
 *
 * `auto-fit` rather than a fixed four, because the strip is not always four:
 * a screen with three facts and a fixed template leaves a quarter of the band
 * empty, which reads as a figure that failed to load.
 *
 * **The hairlines are a `gap` on a ruled ground, not borders on the cells.**
 * A `border-l` on every cell but the first is the obvious spelling and it
 * breaks the moment the strip wraps: the first cell of the second row keeps a
 * border with nothing to its left. A one-pixel grid gap over
 * `--color-rule` draws the same lines and cannot get that wrong at any number
 * of columns.
 *
 * Values are mono because every one of them so far is a figure, an identifier
 * or a timestamp. A value that is genuinely prose should be passed as a node
 * that sets its own family rather than fought with here.
 */
export function FactStrip({ facts, className }: FactStripProps) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-[var(--radius-md)] border border-rule bg-rule",
        className,
      )}
    >
      <dl className="grid grid-cols-2 gap-px md:grid-cols-[repeat(auto-fit,minmax(9rem,1fr))]">
        {facts.map((fact) => (
          <div key={fact.label} className="flex flex-col gap-1 bg-surface px-4 py-2.5">
            <dt className="text-micro font-medium tracking-[0.08em] text-ink-3 uppercase">
              {fact.label}
            </dt>
            <dd className="font-mono text-ui text-ink">{fact.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
